import { CalendarRange, Filter } from "lucide-react";
import { useRef, useState } from "react";
import {
  INSIGHT_MONTHS,
  type DashboardTrendPoint,
  type InsightDeltas,
  type InsightQualitySignals,
  type RouteCostVariance,
} from "../trips/selectors";
import type {
  ContributionStats,
  InsightPeriodMode,
  MonthlyData,
  MonthlyVehicleStats,
} from "../trips/types";
import { CompactSelect, type SelectOption } from "../shared/CompactSelect";
import { FilterPanel } from "../shared/FilterPanel";
import { OperationalTrendChart } from "../shared/OperationalTrendChart";
import { formatCurrency, formatMonth, formatPercent } from "../shared/currency";
import { TablePager } from "../shared/TablePager";
import { usePagedRows } from "../shared/usePagedRows";

type AnalyticsView = "overview" | "performance" | "contributions";

type InsightsPageProps = {
  periodMode: InsightPeriodMode;
  selectedPeriod: string;
  selectedYear: string;
  selectedMonth: string;
  yearOptions: string[];
  monthOptions: string[];
  monthsByYear: Record<string, string[]>;
  data: MonthlyData;
  vehiclePerformance: Array<[string, MonthlyVehicleStats]>;
  partyPerformance: Array<[string, ContributionStats]>;
  routePerformance: Array<[string, ContributionStats]>;
  brokerPerformance: Array<[string, ContributionStats]>;
  deltas: InsightDeltas;
  qualitySignals: InsightQualitySignals;
  routeCostVariance: RouteCostVariance[];
  trend: DashboardTrendPoint[];
  onAllTimeChange: (isAllTime: boolean) => void;
  onYearChange: (year: string) => void;
  onMonthChange: (month: string) => void;
};

const analyticsViews: Array<{ value: AnalyticsView; label: string }> = [
  { value: "overview", label: "Overview" },
  { value: "performance", label: "Performance" },
  { value: "contributions", label: "Contributions" },
];

export function InsightsPage({
  periodMode,
  selectedPeriod,
  selectedYear,
  selectedMonth,
  yearOptions,
  monthsByYear,
  data,
  vehiclePerformance,
  partyPerformance,
  routePerformance,
  brokerPerformance,
  deltas,
  qualitySignals,
  routeCostVariance,
  trend,
  onAllTimeChange,
  onYearChange,
  onMonthChange,
}: InsightsPageProps) {
  const [activeView, setActiveView] = useState<AnalyticsView>("overview");
  const [isPeriodOpen, setIsPeriodOpen] = useState(false);
  const [draftAllTime, setDraftAllTime] = useState(periodMode === "all");
  const [draftYear, setDraftYear] = useState(selectedYear);
  const [draftMonth, setDraftMonth] = useState(selectedMonth);
  const periodTriggerRef = useRef<HTMLButtonElement>(null);
  const hasInsightData =
    vehiclePerformance.length > 0 ||
    partyPerformance.length > 0 ||
    routePerformance.length > 0 ||
    brokerPerformance.length > 0;
  const periodLabel = getPeriodLabel(periodMode, selectedPeriod);
  const yearSelectOptions: SelectOption<string>[] = yearOptions.map((year) => ({
    value: year,
    label: year,
  }));
  const availableDraftMonths = draftYear ? monthsByYear[draftYear] ?? [] : [];
  const monthSelectOptions: SelectOption<string>[] = [
    { value: "", label: "Whole year" },
    ...INSIGHT_MONTHS.filter((month) =>
      availableDraftMonths.includes(month.value)
    ).map((month) => ({ value: month.value, label: month.label })),
  ];

  const openPeriod = () => {
    setDraftAllTime(periodMode === "all");
    setDraftYear(selectedYear);
    setDraftMonth(selectedMonth);
    setIsPeriodOpen(true);
  };

  const applyPeriod = () => {
    if (draftAllTime) {
      onAllTimeChange(true);
    } else {
      onAllTimeChange(false);
      onYearChange(draftYear);
      onMonthChange(draftMonth);
    }
    setIsPeriodOpen(false);
  };

  return (
    <div className="space-y-4">
      <section className="orbit-card grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 p-3 lg:grid-cols-[minmax(0,1fr)_auto_auto]">
        <div className="min-w-0">
          <h2 className="font-semibold text-slate-950">Analytics</h2>
          <p className="truncate text-xs text-slate-500">
            {periodLabel} business performance
          </p>
        </div>
        <button
          ref={periodTriggerRef}
          type="button"
          onClick={openPeriod}
          aria-label={`Change analytics period, currently ${periodLabel}`}
          className="orbit-tab col-start-2 row-start-1 inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-semibold sm:h-9 lg:col-start-3"
        >
          <CalendarRange size={16} className="hidden sm:block" />
          <span className="hidden sm:inline">{periodLabel}</span>
          <Filter size={16} className="sm:hidden" />
        </button>
        <div className="orbit-raised order-3 col-span-2 grid w-full grid-cols-3 p-1 lg:order-none lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:w-auto">
          {analyticsViews.map((view) => (
            <button
              key={view.value}
              type="button"
              onClick={() => setActiveView(view.value)}
              className={`min-h-9 rounded-lg px-2.5 text-xs font-semibold sm:px-3 sm:text-sm ${
                activeView === view.value ? "orbit-tab-active" : "orbit-tab"
              }`}
            >
              {view.label}
            </button>
          ))}
        </div>
      </section>

      {!hasInsightData ? (
        <section className="orbit-card px-4 py-10 text-center">
          <h2 className="font-semibold text-slate-950">
            No data for {periodLabel}
          </h2>
          <p className="mx-auto mt-1 max-w-lg text-sm text-slate-500">
            Select another period, or add reportable trips to unlock this view.
          </p>
        </section>
      ) : null}

      {hasInsightData && activeView === "overview" ? (
        <AnalyticsOverview
          data={data}
          deltas={deltas}
          qualitySignals={qualitySignals}
          trend={trend}
          periodLabel={periodLabel}
        />
      ) : null}

      {hasInsightData && activeView === "performance" ? (
        <PerformanceView
          vehiclePerformance={vehiclePerformance}
          routeCostVariance={routeCostVariance}
        />
      ) : null}

      {hasInsightData && activeView === "contributions" ? (
        <ContributionsView
          partyPerformance={partyPerformance}
          routePerformance={routePerformance}
          brokerPerformance={brokerPerformance}
        />
      ) : null}

      <FilterPanel
        open={isPeriodOpen}
        title="Analytics period"
        triggerRef={periodTriggerRef}
        onClose={() => setIsPeriodOpen(false)}
        onReset={() => {
          setDraftAllTime(false);
          setDraftYear(yearOptions[0] ?? "");
          setDraftMonth("");
        }}
        onApply={applyPeriod}
      >
        <div>
          <p className="mb-1.5 text-xs font-bold uppercase text-slate-500">
            Scope
          </p>
          <div className="orbit-raised grid grid-cols-2 p-1">
            <button
              type="button"
              onClick={() => setDraftAllTime(false)}
              className={`min-h-11 rounded-lg px-3 text-sm font-semibold ${
                draftAllTime ? "orbit-tab" : "orbit-tab-active"
              }`}
            >
              Period
            </button>
            <button
              type="button"
              onClick={() => setDraftAllTime(true)}
              className={`min-h-11 rounded-lg px-3 text-sm font-semibold ${
                draftAllTime ? "orbit-tab-active" : "orbit-tab"
              }`}
            >
              All Time
            </button>
          </div>
        </div>
        <PeriodField label="Year">
          <CompactSelect
            ariaLabel="Analytics year"
            value={draftAllTime ? "" : draftYear}
            options={
              draftAllTime
                ? [{ value: "", label: "None", disabled: true }]
                : yearSelectOptions
            }
            disabled={draftAllTime || yearSelectOptions.length === 0}
            placeholder="No years"
            onChange={(year) => {
              setDraftYear(year);
              setDraftMonth("");
            }}
          />
        </PeriodField>
        <PeriodField label="Month">
          <CompactSelect
            ariaLabel="Analytics month"
            value={draftAllTime ? "" : draftMonth}
            options={
              draftAllTime
                ? [{ value: "", label: "None", disabled: true }]
                : monthSelectOptions
            }
            disabled={draftAllTime || !draftYear}
            onChange={setDraftMonth}
          />
        </PeriodField>
      </FilterPanel>
    </div>
  );
}

function AnalyticsOverview({
  data,
  deltas,
  qualitySignals,
  trend,
  periodLabel,
}: {
  data: MonthlyData;
  deltas: InsightDeltas;
  qualitySignals: InsightQualitySignals;
  trend: DashboardTrendPoint[];
  periodLabel: string;
}) {
  const marginPct = data.totalHire > 0 ? data.totalProfit / data.totalHire : 0;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[0.8fr_1.2fr]">
      <section className="orbit-card overflow-hidden">
        <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0 xl:grid-cols-1 xl:divide-x-0 xl:divide-y">
          <InsightMetric
            label="Revenue"
            value={formatCurrency(data.totalHire)}
            detail={formatMoneyDelta(deltas.totalHire.value)}
          />
          <InsightMetric
            label="Expense"
            value={formatCurrency(data.totalExp)}
            detail={formatMoneyDelta(deltas.totalExp.value)}
            tone="rose"
          />
          <InsightMetric
            label="Net profit"
            value={formatCurrency(data.totalProfit)}
            detail={`${formatPercent(marginPct)} margin / ${formatMoneyDelta(
              deltas.totalProfit.value
            )}`}
            tone="emerald"
          />
        </div>
        <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100">
          <QualityMetric
            label="Repeat parties"
            value={formatPercent(qualitySignals.repeatPartyRate)}
          />
          <QualityMetric
            label="Low margin"
            value={formatPercent(qualitySignals.lowMarginShare)}
          />
          <QualityMetric
            label="Avg settle"
            value={`${Math.round(qualitySignals.averageDaysToSettle)}d`}
          />
        </div>
      </section>

      <section className="orbit-card min-w-0 p-3 sm:p-4">
        <div className="mb-2">
          <h2 className="text-sm font-semibold text-slate-950">
            Operational trend
          </h2>
          <p className="text-xs text-slate-500">
            Last six months, alongside {periodLabel.toLowerCase()} results.
          </p>
        </div>
        <OperationalTrendChart trend={trend} />
      </section>
    </div>
  );
}

function PerformanceView({
  vehiclePerformance,
  routeCostVariance,
}: {
  vehiclePerformance: Array<[string, MonthlyVehicleStats]>;
  routeCostVariance: RouteCostVariance[];
}) {
  const {
    page,
    pageCount,
    pageRows,
    nextPage,
    previousPage,
  } = usePagedRows(vehiclePerformance);
  const bestVehicle = vehiclePerformance[0];
  const weakestVehicle = [...vehiclePerformance].sort(
    ([, a], [, b]) => a.profit - b.profit
  )[0];

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.25fr_0.75fr]">
      <section className="orbit-card min-w-0 overflow-hidden">
        <div className="border-b border-slate-200 px-3 py-2.5">
          <h2 className="font-semibold text-slate-950">Vehicle performance</h2>
          <p className="text-xs text-slate-500">Profitability by vehicle</p>
        </div>
        <div className="divide-y divide-slate-100 lg:hidden">
          {pageRows.map(([vehicle, stats]) => (
            <article key={vehicle} className="px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-950">
                    {vehicle}
                  </p>
                  <p className="text-xs text-slate-500">{stats.trips} trips</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-950">
                    {formatCurrency(stats.profit)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatPercent(stats.marginPct)} margin
                  </p>
                </div>
              </div>
              <p className="mt-1.5 text-xs text-slate-500">
                Revenue {formatCurrency(stats.hire)} / Cost {formatCurrency(stats.expense)}
              </p>
            </article>
          ))}
        </div>
        <div className="hidden lg:block">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="w-[28%] px-3 py-2.5">Vehicle</th>
                <th className="w-[10%] px-3 py-2.5 text-center">Trips</th>
                <th className="w-[18%] px-3 py-2.5 text-right">Revenue</th>
                <th className="w-[18%] px-3 py-2.5 text-right">Expense</th>
                <th className="w-[16%] px-3 py-2.5 text-right">Profit</th>
                <th className="w-[10%] px-3 py-2.5 text-right">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.map(([vehicle, stats]) => (
                <tr key={vehicle}>
                  <td className="truncate px-3 py-2.5 font-semibold text-slate-950">
                    {vehicle}
                  </td>
                  <td className="px-3 py-2.5 text-center">{stats.trips}</td>
                  <td className="px-3 py-2.5 text-right">{formatCurrency(stats.hire)}</td>
                  <td className="px-3 py-2.5 text-right text-rose-700">
                    {formatCurrency(stats.expense)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold">
                    {formatCurrency(stats.profit)}
                  </td>
                  <td className="px-3 py-2.5 text-right">{formatPercent(stats.marginPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager
          page={page}
          pageCount={pageCount}
          totalRows={vehiclePerformance.length}
          onPrevious={previousPage}
          onNext={nextPage}
        />
      </section>

      <div className="space-y-4">
        <section className="orbit-card overflow-hidden">
          <SignalRow
            label="Best contribution"
            title={bestVehicle?.[0] ?? "No vehicle"}
            detail={
              bestVehicle
                ? `${formatCurrency(bestVehicle[1].profit)} across ${bestVehicle[1].trips} trips`
                : "No performance data"
            }
            tone="emerald"
          />
          <SignalRow
            label="Needs review"
            title={weakestVehicle?.[0] ?? "No vehicle"}
            detail={
              weakestVehicle
                ? `${formatCurrency(weakestVehicle[1].profit)} across ${weakestVehicle[1].trips} trips`
                : "No performance data"
            }
            tone="rose"
          />
        </section>
        {routeCostVariance.length > 0 ? (
          <section className="orbit-card overflow-hidden">
            <div className="border-b border-slate-200 px-3 py-2.5">
              <h2 className="font-semibold text-slate-950">Route cost variance</h2>
              <p className="text-xs text-slate-500">Routes with at least 3 trips</p>
            </div>
            <div className="divide-y divide-slate-100">
              {routeCostVariance.slice(0, 5).map((route) => (
                <article key={route.route} className="px-3 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-950">
                        {route.route}
                      </p>
                      <p className="text-xs text-slate-500">{route.trips} trips</p>
                    </div>
                    <p
                      className={`text-sm font-bold ${
                        route.variancePct > 0 ? "text-rose-700" : "text-emerald-700"
                      }`}
                    >
                      {formatPercent(route.variancePct)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}

function ContributionsView({
  partyPerformance,
  routePerformance,
  brokerPerformance,
}: {
  partyPerformance: Array<[string, ContributionStats]>;
  routePerformance: Array<[string, ContributionStats]>;
  brokerPerformance: Array<[string, ContributionStats]>;
}) {
  return (
    <section className="orbit-card overflow-hidden">
      <div className="grid grid-cols-1 divide-y divide-slate-100 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
        <ContributionPanel title="Parties" rows={partyPerformance} />
        <ContributionPanel title="Routes" rows={routePerformance} />
        <ContributionPanel title="Brokers" rows={brokerPerformance} />
      </div>
    </section>
  );
}

function InsightMetric({
  label,
  value,
  detail,
  tone = "blue",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: "blue" | "emerald" | "rose";
}) {
  const toneClass =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "rose"
        ? "text-rose-700"
        : "text-slate-950";
  return (
    <div className="min-w-0 px-4 py-3">
      <p className="text-[11px] font-bold uppercase text-slate-500">{label}</p>
      <p className={`mt-1 truncate text-xl font-bold ${toneClass}`}>{value}</p>
      <p className="mt-0.5 truncate text-xs text-slate-500">{detail}</p>
    </div>
  );
}

function QualityMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 px-2 py-3 text-center">
      <p className="truncate text-[10px] font-bold uppercase text-slate-500">{label}</p>
      <p className="mt-1 truncate text-sm font-bold text-slate-950">{value}</p>
    </div>
  );
}

function SignalRow({
  label,
  title,
  detail,
  tone,
}: {
  label: string;
  title: string;
  detail: string;
  tone: "emerald" | "rose";
}) {
  return (
    <div className="border-b border-slate-100 px-3 py-3 last:border-b-0">
      <span className={`orbit-tone-${tone} rounded-full px-2 py-1 text-xs font-semibold`}>
        {label}
      </span>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="min-w-0 truncate font-bold text-slate-950">{title}</p>
        <p className="shrink-0 text-xs text-slate-500">{detail}</p>
      </div>
    </div>
  );
}

function ContributionPanel({
  title,
  rows,
}: {
  title: string;
  rows: Array<[string, ContributionStats]>;
}) {
  return (
    <div className="min-w-0">
      <div className="border-b border-slate-200 px-3 py-2.5">
        <h2 className="font-semibold text-slate-950">{title}</h2>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.slice(0, 8).map(([label, stats]) => (
          <article key={label} className="flex items-center justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-950">{label}</p>
              <p className="text-xs text-slate-500">
                {stats.trips} trips / {formatPercent(stats.marginPct)} margin
              </p>
            </div>
            <p className="shrink-0 text-sm font-bold text-slate-950">
              {formatCurrency(stats.profit)}
            </p>
          </article>
        ))}
        {rows.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-slate-500">
            No contribution yet.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function PeriodField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-bold uppercase text-slate-500">{label}</p>
      {children}
    </div>
  );
}

const getPeriodLabel = (mode: InsightPeriodMode, period: string): string => {
  if (mode === "all") return "All time";
  if (mode === "year") return period || "No year";
  return period ? formatMonth(period) : "No month";
};

const formatMoneyDelta = (value: number): string => {
  if (value === 0) return "No change vs prior";
  return `${value > 0 ? "+" : ""}${formatCurrency(value)} vs prior`;
};
